import {
    EnumButtonName,
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
import CHLink from '@rhg/components/ch-link';
import { FC, MouseEvent, forwardRef, useMemo } from 'react';
import { TrackFn, useTracking } from '../..';
import { useAnalyticsTitle } from '../use-analytics-title';

export type ButtonAnalyticsProps<T> = T & {
    /**
     * Analytics button name
     */
    trackButton: EnumButtonName;
    /**
     * Analytics feature name (defaults to the feature for the current route)
     */
    trackFeature?: EnumFeature;
    /**
     * Analytics screen name (defaults to screen for the current route)
     */
    trackScreen?: EnumScreenName;
    /**
     * Any additional props
     */
    trackProps?: EventProperties;

    /**
     * Track analytics button click events after processing the provided
     * onClick handler. This is useful if you need to set super-properties
     * before the event is sent.
     */
    trackAfterOnClick?: boolean;

    /**
     * Appends a unique identified to differentiate buttons with the
     * same tracking name. (used for e2e tests)
     */
    testId?: string;

    /**
     * Pass true to immediately post events to analytics destination
     */
    trackAndFlushEvents?: boolean;
};

export type ButtonWithoutAnalyticsProps<T> = T & {
    skipTracking: boolean;
};

const clickEventName = 'Button : Clicked';

interface OnClickOverride {
    e?: MouseEvent<HTMLElement>;
    onClick?: (...args: any[]) => void;
    track: TrackFn;
    flush?: () => void;
    currentFeature: EnumFeature;
    currentScreenName: EnumScreenName;
}

export const _onAnalyticsClickOverride = ({
    trackAfterOnClick,
    onClick,
    track,
    flush,
    trackButton,
    trackFeature,
    trackScreen,
    trackProps,
    currentFeature,
    currentScreenName,
    e,
    trackAndFlushEvents = false,
}: ButtonAnalyticsProps<OnClickOverride>) => {
    if (trackAfterOnClick) {
        onClick?.(e);
    }

    const screenName = trackScreen ?? currentScreenName;

    track(`${clickEventName}`, {
        ButtonName: trackButton!,
        Feature: trackFeature ?? currentFeature,
        ScreenName: screenName,
        ...(trackProps ?? {}),
    });
    if (trackAndFlushEvents && flush) {
        flush();
    }
    if (!trackAfterOnClick) {
        onClick?.(e);
    }
};

export function withButtonAnalytics<
    T extends { onClick?: (...args: any[]) => void }
>(Component: FC<T>) {
    function AnalyticsButton(
        props: ButtonAnalyticsProps<T> & { forwardedRef: any }
    ) {
        const {
            forwardedRef,
            trackButton,
            trackScreen,
            trackFeature,
            trackProps,
            testId,
            trackAfterOnClick,
            onClick,
            trackAndFlushEvents,
            ...others
        } = props as ButtonAnalyticsProps<T> & { forwardedRef: any };
        const {
            track,
            flush,
            state: { currentFeature, currentScreenName },
        } = useTracking();

        const handleOnClick = (e: MouseEvent<HTMLElement>) =>
            _onAnalyticsClickOverride({
                trackAfterOnClick,
                onClick,
                track,
                flush,
                trackButton,
                trackFeature,
                trackScreen,
                trackProps,
                currentFeature,
                currentScreenName,
                trackAndFlushEvents: trackAndFlushEvents,
                e,
            });

        const { analyticsTitleProps } = useAnalyticsTitle(
            `${trackButton}  |  Click${trackButton}`
        );

        return useMemo(
            () => (
                <Component
                    data-testid={`BUTTON_${trackButton}${
                        testId ? '_' + testId : ''
                    }`}
                    {...(others as typeof props)}
                    {...analyticsTitleProps}
                    ref={forwardedRef}
                    onClick={handleOnClick}
                />
            ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [forwardedRef, others, analyticsTitleProps, trackButton]
        );
    }

    /**
     * HOC components do not forward `ref` by default so we setup the `forwardedRef` prop
     * and pass it to the nested function component and map it back to `ref`
     */
    // TODO: EVE-6055 figure out the props mismatch
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return forwardRef(function ForwardedAnalyticsButton(
        props: ButtonAnalyticsProps<T> | ButtonWithoutAnalyticsProps<T>,
        ref
    ) {
        // Use <a> tag for download actions
        const linkComponentProps =
            'href' in props && props.href
                ? 'download' in props
                    ? { component: 'a' }
                    : { component: CHLink }
                : {};
        if ((props as ButtonWithoutAnalyticsProps<T>).skipTracking) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { skipTracking, ...others } =
                props as ButtonWithoutAnalyticsProps<T>;
            return (
                <Component
                    {...(others as typeof props)}
                    {...linkComponentProps}
                    ref={ref}
                />
            );
        }
        return (
            <AnalyticsButton
                {...(props as ButtonAnalyticsProps<T>)}
                {...linkComponentProps}
                forwardedRef={ref}
            />
        );
    });
}
